import {
  FileText,
  Users,
  UserCheck,
  Calendar,
  Megaphone,
  Settings,
  StickyNote,
  BarChart3,
  DollarSign,
  ClipboardCheck
} from 'lucide-react';
import { WorkspaceTab } from '../../../types/workspace';
import { lazy } from 'react';

// Lazy load tab components for better performance
const BillTrackingTab = lazy(() => {
  return import('../tabs/BillTrackingTab').catch(err => {
    console.error('❌ Failed to load BillTrackingTab:', err);
    throw err;
  });
});
const NotesTab = lazy(() => import('../tabs/NotesTab'));
const LegislatorsTab = lazy(() => import('../tabs/LegislatorsTab'));
const CandidatesTab = lazy(() => import('../tabs/CandidatesTab'));
const InitiativesTab = lazy(() => import('../tabs/InitiativesTab'));
const CalendarTab = lazy(() => {
  return import('../tabs/CalendarTab').catch(err => {
    console.error('❌ Failed to load CalendarTab:', err);
    throw err;
  });
});
const ReportsTab = lazy(() => import('../tabs/ReportsTab'));
const OutreachTab = lazy(() => import('../tabs/OutreachTab'));
const BudgetTrackingTab = lazy(() => import('../tabs/BudgetTrackingTab'));
const ManageWorkspaceTab = lazy(() => import('../tabs/ManageWorkspaceTab'));

/**
 * Central configuration for all workspace tabs
 * This is the single source of truth for tab definitions
 */
export const getWorkspaceTabs = (): WorkspaceTab[] => [
  {
    id: 'bills',
    label: 'Bill Tracking',
    icon: FileText,
    component: BillTrackingTab,
    enabled: true,
    permissions: ['canView'],
    lazy: true,
    order: 1,
    description: 'Track and monitor legislation relevant to your organization'
  },
  {
    id: 'notes',
    label: 'Notes',
    icon: StickyNote,
    component: NotesTab,
    enabled: true,
    permissions: ['canView'],
    lazy: true,
    order: 2,
    description: 'Create and manage notes related to bills, legislators, and campaigns'
  },
  {
    id: 'calendar',
    label: 'Calendar',
    icon: Calendar,
    component: CalendarTab,
    enabled: true,
    permissions: ['canView'],
    lazy: true,
    order: 3,
    description: 'View legislative calendar and important dates'
  },
  {
    id: 'reports',
    label: 'Reports',
    icon: BarChart3,
    component: ReportsTab,
    enabled: true,
    permissions: ['canView'],
    lazy: true,
    order: 4,
    description: 'Generate standard and custom reports on workspace activity'
  },
  {
    id: 'call-to-action',
    label: 'Outreach',
    icon: Megaphone,
    component: OutreachTab,
    enabled: true,
    // Everyone who can open the workspace sees Outreach; only an org admin of
    // the owner org acts on it — the tab renders read-only for everyone else
    // (OUTREACH_CONTRIBUTOR_READ_ONLY_PRD.md §5.4; the server enforces).
    permissions: ['canView'],
    lazy: true,
    order: 5,
    description: 'Manage advocacy campaigns, surveys, and public engagement'
  },
  {
    id: 'budget',
    label: 'Budget Tracking',
    icon: DollarSign,
    component: BudgetTrackingTab,
    enabled: false,
    permissions: ['canView'],
    lazy: true,
    order: 6,
    description: 'Track and monitor budget items relevant to your clients'
  },
  // NOTE: Vote Predictions is intentionally NOT a tab. It is reached only from a tracked
  // bill in the Bill Tracking tab, which opens VotePredictionsModal for that specific bill.
  {
    id: 'legislators',
    label: 'Legislators',
    icon: Users,
    component: LegislatorsTab,
    enabled: true,
    permissions: ['canView'],
    lazy: true,
    order: 7,
    description: 'Track and analyze legislator positions and voting patterns'
  },
  {
    id: 'candidates',
    label: 'Candidates',
    icon: UserCheck,
    component: CandidatesTab,
    enabled: true,
    permissions: ['canView'],
    lazy: true,
    order: 8,
    description: 'Track political candidates and their positions'
  },
  {
    id: 'initiatives',
    label: 'Ballot Initiatives',
    icon: ClipboardCheck,
    component: InitiativesTab,
    enabled: true,
    permissions: ['canView'],
    // 8.5 slots between Candidates (8) and Manage (9) without renumbering Manage —
    // existing workspaces' stored orders for Manage would otherwise tie with this tab.
    order: 8.5,
    lazy: true,
    description: 'Track ballot measures and set workspace positions'
  },
  {
    id: 'manage',
    label: 'Manage Workspace',
    icon: Settings,
    component: ManageWorkspaceTab,
    enabled: true,
    permissions: ['canManage'],
    lazy: true,
    order: 9,
    description: 'Configure workspace settings, members, and tab visibility'
  }
];

/**
 * Get default tab configuration for new workspaces
 */
export const getDefaultTabConfiguration = () => {
  const tabs = getWorkspaceTabs();
  const config: Record<string, any> = {};
  
  tabs.forEach(tab => {
    config[tab.id] = {
      enabled: tab.enabled,
      order: tab.order,
      visible: tab.enabled, // Match visible with enabled state
      permissions: tab.permissions || []
    };
  });
  
  return config;
};

/**
 * Get tabs available for a specific permission level
 */
export const getTabsForPermissions = (permissions: string[]): WorkspaceTab[] => {
  const allTabs = getWorkspaceTabs();
  
  return allTabs.filter(tab => {
    if (!tab.permissions || tab.permissions.length === 0) return true;
    return tab.permissions.some(permission => permissions.includes(permission));
  });
};
